<template>
    <o-modal v-if="isVisible" @close="close">
        <template v-slot:header>
            <h5 class="modal-title">
                {{ isEditMode ? 'Edit Product' : `Add Product${merchantName ? ' to ' + merchantName : ''}` }}
            </h5>
        </template>

        <template v-slot:body>
            <!-- Loading state -->
            <div v-if="isFetching" class="loading-state">
                <div class="spinner-border text-primary" role="status"></div>
                <span class="ms-2">Loading product data...</span>
            </div>

            <template v-else>
                <!-- Error Alert -->
                <div v-if="error" class="alert alert-danger mb-3">
                    <i class="fas fa-exclamation-circle me-2"></i>{{ error }}
                </div>

                <!-- Merchant -->
                <div class="form-group mb-3">
                    <label for="merchant_id" class="form-label">
                        Merchant <span class="text-danger">*</span>
                    </label>
                    <select
                        id="merchant_id"
                        class="form-select"
                        v-model="form.merchant_id"
                        :class="{ 'is-invalid': errors.merchant_id }"
                        :disabled="merchantIdPrefilled"
                    >
                        <option value="">Select merchant</option>
                        <option v-for="m in merchantOptions" :key="m.value" :value="m.value">
                            {{ m.text }}
                        </option>
                    </select>
                    <div v-if="errors.merchant_id" class="invalid-feedback">{{ errors.merchant_id }}</div>
                </div>

                <!-- Product Name -->
                <div class="form-group mb-3">
                    <label for="product_name" class="form-label">
                        Product Name <span class="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="product_name"
                        class="form-control"
                        v-model="form.name"
                        :class="{ 'is-invalid': errors.name }"
                        placeholder="Enter product name"
                        autocomplete="off"
                    />
                    <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                </div>

                <!-- Price -->
                <div class="form-group mb-3">
                    <label for="product_price" class="form-label">
                        Price (IDR) <span class="text-danger">*</span>
                    </label>
                    <div class="input-group" :class="{ 'is-invalid': errors.price }">
                        <span class="input-group-text">Rp</span>
                        <input
                            type="text"
                            id="product_price"
                            class="form-control"
                            v-model="priceDisplay"
                            :class="{ 'is-invalid': errors.price }"
                            placeholder="0"
                            autocomplete="off"
                            maxlength="18"
                            @input="onPriceInput"
                            @blur="onPriceBlur"
                        />
                    </div>
                    <div v-if="errors.price" class="invalid-feedback d-block">{{ errors.price }}</div>
                    <div class="form-text">
                        <i class="fas fa-info-circle me-1"></i>Enter numeric value, e.g. 50000
                    </div>
                </div>

                <!-- Actions -->
                <div class="d-flex justify-content-end gap-2 mt-4">
                    <button type="button" class="btn btn-secondary" @click="close" :disabled="isSaving">
                        Cancel
                    </button>
                    <button type="button" class="btn btn-primary" @click="save" :disabled="isSaving">
                        {{ isSaving ? 'Saving...' : (isEditMode ? 'Update' : 'Save') }}
                    </button>
                </div>
            </template>
        </template>
    </o-modal>
</template>

<script>
import { reactive, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { OModal } from '@/components/OmnihControl';

export default {
    name: 'ProductFormModal',
    components: { OModal },
    props: {
        isVisible: {
            type: Boolean,
            default: false
        },
        merchantId: {
            type: [String, Number],
            default: null
        },
        productId: {
            type: [String, Number],
            default: null
        },
        merchants: {
            type: Array,
            default: () => []
        }
    },
    emits: ['close', 'save'],

    setup(props, { emit }) {
        const store = useStore();

        const form = reactive({
            name: '',
            price: '',       // raw numeric string
            merchant_id: ''
        });

        const priceDisplay = ref(''); // formatted display string
        const errors = reactive({ name: '', price: '', merchant_id: '' });
        const error = ref('');
        const isFetching = ref(false);
        const isSaving = ref(false);

        const isEditMode = computed(() => !!props.productId);
        const merchantIdPrefilled = computed(() => !!props.merchantId && !isEditMode.value);

        const merchantOptions = computed(() =>
            (props.merchants || []).map(m => ({
                value: m.id,
                text: m.name || m.business_profile?.name || m.id
            }))
        );

        const merchantName = computed(() => {
            if (!props.merchantId) return '';
            const m = (props.merchants || []).find(m => m.id === props.merchantId);
            return m ? (m.name || m.business_profile?.name || m.id) : '';
        });


        const onPriceInput = (e) => {
            let raw = e.target.value.replace(/\D/g, '');
            // Limit to 15 digits
            if (raw.length > 15) {
                raw = raw.substring(0, 15);
            }
            form.price = raw;
            // Only show empty if raw is empty string, but show 0 if raw is '0'
            priceDisplay.value = raw !== '' ? Number(raw).toLocaleString('id-ID') : '';
        };

        const onPriceBlur = () => {
            priceDisplay.value = form.price !== '' ? Number(form.price).toLocaleString('id-ID') : '';
        };

        const resetForm = () => {
            form.name = '';
            form.price = '';
            form.merchant_id = props.merchantId || '';
            priceDisplay.value = '';
            Object.keys(errors).forEach(k => errors[k] = '');
            error.value = '';
        };

        const loadProduct = async (id) => {
            isFetching.value = true;
            error.value = '';
            try {
                const product = await store.dispatch('Product/read_single', id);
                if (product) {
                    form.name = product.name || '';
                    // Check for null/undefined to allow 0
                    const rawPrice = (product.price !== null && product.price !== undefined) 
                        ? String(product.price).replace(/\D/g, '') 
                        : '';
                    form.price = rawPrice;
                    priceDisplay.value = rawPrice !== '' ? Number(rawPrice).toLocaleString('id-ID') : '';
                    form.merchant_id = product.merchant_id || props.merchantId || '';
                }
            } catch (err) {
                error.value = 'Failed to load product data.';
                console.error(err);
            } finally {
                isFetching.value = false;
            }
        };

        watch(
            () => props.isVisible,
            async (visible) => {
                if (visible) {
                    resetForm();
                    if (props.productId) {
                        await loadProduct(props.productId);
                    }
                }
            },
            { immediate: false }
        );

        const validate = () => {
            Object.keys(errors).forEach(k => errors[k] = '');
            let valid = true;

            if (!form.merchant_id) {
                errors.merchant_id = 'Merchant is required';
                valid = false;
            }
            if (!form.name || !form.name.trim()) {
                errors.name = 'Product name is required';
                valid = false;
            } else if (form.name.trim().length > 255) {
                errors.name = 'Product name must not exceed 255 characters';
                valid = false;
            }
            if (form.price === '' || form.price === null || form.price === undefined) {
                errors.price = 'Price is required';
                valid = false;
            } else if (isNaN(Number(form.price)) || Number(form.price) < 0) {
                errors.price = 'Price must be a valid positive number';
                valid = false;
            }

            return valid;
        };

        const save = async () => {
            if (!validate()) return;

            isSaving.value = true;
            error.value = '';

            const payload = {
                name: form.name.trim(),
                price: form.price,
                merchant_id: form.merchant_id
            };

            try {
                if (isEditMode.value) {
                    await store.dispatch('Product/update', {
                        data: { ...payload, price: String(form.price), id: props.productId }
                    });
                } else {
                    await store.dispatch('Product/create', { data: payload });
                }
                emit('save', form.merchant_id, isEditMode.value);
                close();
            } catch (err) {
                console.error(err);
                const errData = err.response?.data;
                if (errData?.errors) {
                    const apiErrors = errData.errors;
                    if (apiErrors.name) errors.name = apiErrors.name[0];
                    if (apiErrors.price) errors.price = apiErrors.price[0];
                    if (apiErrors.merchant_id) errors.merchant_id = apiErrors.merchant_id[0];
                } else if (errData?.error) {
                    try {
                        const parsed = typeof errData.error === 'string'
                            ? JSON.parse(errData.error)
                            : errData.error;
                        if (parsed.name) errors.name = parsed.name[0];
                        if (parsed.price) errors.price = parsed.price[0];
                        if (parsed.merchant_id) errors.merchant_id = parsed.merchant_id[0];
                    } catch {
                        error.value = String(errData.error);
                    }
                } else {
                    error.value = errData?.message || 'An error occurred while saving.';
                }
            } finally {
                isSaving.value = false;
            }
        };

        const close = () => {
            resetForm();
            emit('close');
        };

        return {
            form,
            priceDisplay,
            errors,
            error,
            isFetching,
            isSaving,
            isEditMode,
            merchantIdPrefilled,
            merchantOptions,
            merchantName,
            onPriceInput,
            onPriceBlur,
            save,
            close
        };
    }
};
</script>

<style scoped>
.form-label {
    font-weight: 500;
    color: #333;
}

.text-danger {
    color: #dc3545;
}

.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    display: block;
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.form-text {
    font-size: 0.875rem;
    color: #6c757d;
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 1rem;
}

.text-primary {
    color: var(--primary-color, #F93E3E) !important;
}

.btn-primary {
    background-color: var(--primary-color, #F93E3E) !important;
    border-color: var(--primary-color, #F93E3E) !important;
    color: #ffffff !important;
    transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
    filter: brightness(0.9) !important;
    color: #ffffff !important;
}

.btn-secondary {
    background-color: var(--secondary-color, #FEE9E9) !important;
    border-color: var(--secondary-color, #FEE9E9) !important;
    color: var(--primary-color, #F93E3E) !important;
    transition: all 0.2s ease;
}

.btn-secondary:hover:not(:disabled) {
    border-color: var(--primary-color, #F93E3E) !important;
    filter: brightness(0.95) !important;
    color: var(--primary-color, #F93E3E) !important;
}

.form-control:focus,
.form-select:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 0 0 0.25rem var(--secondary-color, #FEE9E9) !important;
}
</style>
